import Phaser from "phaser";

import { ARENA_ART, ARENA_ASSET_COUNT, ARENA_PRELOAD_ASSETS } from "../config/artAssets";
import {
  ARENA_PRESENTATION,
  arenaBackgroundLayout,
  arenaShotTiming,
  arenaTargetLayout,
  type ArenaVisualTarget,
} from "../config/arenaVisualConfig";
import type { PvpMatchState, PvpRole, PvpTurnResult } from "../domain/types";

export type PenaltyArenaVisualState = {
  match: PvpMatchState;
  reducedMotion: boolean;
  paused: boolean;
};

export type PenaltyArenaTargetLayout = {
  role: PvpRole;
  targets: readonly ArenaVisualTarget[];
};

export type PenaltyArenaLoadProgress = {
  phase: "assets" | "scene";
  progress: number;
  loaded: number;
  total: number;
};

export type PenaltyArenaSceneCallbacks = {
  onReady(): void;
  onLoadProgress(progress: PenaltyArenaLoadProgress): void;
  onAssetError?(key: string): void;
  onTargetLayout(layout: PenaltyArenaTargetLayout): void;
};

type Point = { x: number; y: number };

type TargetCue = {
  root: Phaser.GameObjects.Container;
  halo: Phaser.GameObjects.Arc;
  plate: Phaser.GameObjects.Arc;
  glyph: Phaser.GameObjects.Triangle;
  direction: "left" | "centre" | "right";
  baseScale: number;
};

type ActiveShot = {
  result: PvpTurnResult;
  role: PvpRole;
  startedAt: number;
  durationMs: number;
  impactHoldMs: number;
  keeperDelayMs: number;
  keeperMoveMs: number;
  start: Point;
  target: Point;
  keeperStart: Point;
  keeperTarget: Point;
  initialVelocity: Point;
  gravity: number;
  saved: boolean;
  completed: boolean;
};

const PLAYER_STRIKER_FOOT = { x: 0.79, y: 0.92 } as const;
const OPPONENT_WINDUP_SUPPORT = { x: 0.14, y: 0.985 } as const;
const OPPONENT_CONTACT_SUPPORT = { x: 0.7, y: 0.985 } as const;
const DIRECTIONS = ["left", "centre", "right"] as const;

function clampZone(zone: number): number {
  return Phaser.Math.Clamp(Math.round(zone), 0, 2);
}

function easeOutCubic(value: number): number {
  return 1 - (1 - value) ** 3;
}

function roleFromMatch(match: PvpMatchState): PvpRole {
  return match.currentTurn?.role ?? match.history.at(-1)?.role ?? "shooter";
}

export class PenaltyArenaScene extends Phaser.Scene {
  readonly #callbacks: PenaltyArenaSceneCallbacks;
  readonly #renderScale: number;
  #visualState: PenaltyArenaVisualState;
  #visualRole: PvpRole;
  #layoutWidth = 0;
  #layoutHeight = 0;
  #presentedMatchId: string | null;
  #lastPresentedTurn = -1;
  #activeShot: ActiveShot | null = null;
  #pendingResult: Exclude<PvpMatchState["result"], null | "cancelled"> | null = null;
  #ambientTweens: Phaser.Tweens.Tween[] = [];
  #contactTimer: Phaser.Time.TimerEvent | null = null;

  #attackKeeperHome: Point = { x: 0, y: 0 };
  #attackBallHome: Point = { x: 0, y: 0 };
  #attackStrikerHome: Point = { x: 0, y: 0 };
  #defenseKeeperHome: Point = { x: 0, y: 0 };
  #defenseBallHome: Point = { x: 0, y: 0 };
  #defenseOpponentHome: Point = { x: 0, y: 0 };
  #defenseOpponentContactHome: Point = { x: 0, y: 0 };

  #attackBackground!: Phaser.GameObjects.Image;
  #defenseBackground!: Phaser.GameObjects.Image;
  #goalNet!: Phaser.GameObjects.Image;
  #shade!: Phaser.GameObjects.Rectangle;
  #flash!: Phaser.GameObjects.Rectangle;
  #roleWash!: Phaser.GameObjects.Rectangle;
  #targetCues: TargetCue[] = [];

  #attackKeeperReady!: Phaser.GameObjects.Image;
  #attackKeeperDive!: Phaser.GameObjects.Image;
  #attackKeeperCentre!: Phaser.GameObjects.Image;
  #playerStriker!: Phaser.GameObjects.Image;
  #opponentStrikerWindup!: Phaser.GameObjects.Image;
  #opponentStrikerContact!: Phaser.GameObjects.Image;
  #defenseKeeperReady!: Phaser.GameObjects.Image;
  #defenseKeeperDiveLeft!: Phaser.GameObjects.Image;
  #defenseKeeperDiveRight!: Phaser.GameObjects.Image;
  #defenseKeeperCentre!: Phaser.GameObjects.Image;
  #ball!: Phaser.GameObjects.Image;
  #trail: Phaser.GameObjects.Rectangle[] = [];
  #sparks: Phaser.GameObjects.Rectangle[] = [];
  #ceremonyBars: Phaser.GameObjects.Rectangle[] = [];

  constructor(
    callbacks: PenaltyArenaSceneCallbacks,
    visualState: PenaltyArenaVisualState,
    renderScale: number,
  ) {
    super({ key: "PenaltyArena" });
    this.#callbacks = callbacks;
    this.#visualState = visualState;
    this.#visualRole = roleFromMatch(visualState.match);
    this.#presentedMatchId = visualState.match.matchId;
    this.#renderScale = renderScale;
  }

  preload(): void {
    const total = ARENA_ASSET_COUNT;
    this.#callbacks.onLoadProgress({ phase: "assets", progress: 0, loaded: 0, total });
    this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: Phaser.Loader.File) => {
      this.#callbacks.onAssetError?.(file.key);
    });
    this.load.on(Phaser.Loader.Events.PROGRESS, (progress: number) => {
      this.#callbacks.onLoadProgress({
        phase: "assets",
        progress,
        loaded: Math.min(total, Math.round(progress * total)),
        total,
      });
    });
    ARENA_PRELOAD_ASSETS.forEach((asset) => this.load.image(asset.key, asset.url));
  }

  create(): void {
    this.#callbacks.onLoadProgress({ phase: "scene", progress: 1, loaded: ARENA_ASSET_COUNT, total: ARENA_ASSET_COUNT });
    this.#attackBackground = this.add.image(0, 0, ARENA_ART.background.key).setDepth(0);
    this.#defenseBackground = this.add.image(0, 0, ARENA_ART.defenseBackground.key).setDepth(0).setAlpha(0);
    this.#shade = this.add.rectangle(0, 0, 10, 10, 0x022d35, 0.09).setOrigin(0).setDepth(1);

    this.#opponentStrikerWindup = this.add.image(0, 0, ARENA_ART.opponentStrikerWindup.key).setDepth(2);
    this.#opponentStrikerContact = this.add.image(0, 0, ARENA_ART.opponentStrikerContact.key).setDepth(2).setVisible(false);
    this.#goalNet = this.add.image(0, 0, ARENA_ART.goalNet.key).setDepth(3);
    this.#attackKeeperReady = this.add.image(0, 0, ARENA_ART.goalkeeperReady.key).setDepth(5);
    this.#attackKeeperDive = this.add.image(0, 0, ARENA_ART.goalkeeperDive.key).setDepth(6).setVisible(false);
    this.#attackKeeperCentre = this.add.image(0, 0, ARENA_ART.goalkeeperCentreSave.key).setDepth(6).setVisible(false);

    this.#targetCues = DIRECTIONS.map((direction) => this.#createTargetCue(direction));

    this.#playerStriker = this.add.image(0, 0, ARENA_ART.strikerKick.key).setDepth(9);
    this.#defenseKeeperReady = this.add.image(0, 0, ARENA_ART.defenseGoalkeeperReady.key).setDepth(11);
    this.#defenseKeeperDiveLeft = this.add.image(0, 0, ARENA_ART.defenseGoalkeeperDiveLeft.key).setDepth(12).setVisible(false);
    this.#defenseKeeperDiveRight = this.add.image(0, 0, ARENA_ART.defenseGoalkeeperDiveRight.key).setDepth(12).setVisible(false);
    this.#defenseKeeperCentre = this.add.image(0, 0, ARENA_ART.defenseGoalkeeperBlockCentre.key).setDepth(12).setVisible(false);
    this.#ball = this.add.image(0, 0, ARENA_ART.ball.key).setDepth(10);

    this.#trail = Array.from({ length: 9 }, (_, index) =>
      this.add
        .rectangle(0, 0, Math.max(2, 8 - index * 0.55), 2, index % 2 === 0 ? 0xc2fff4 : 0xffd38b, 0)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(10),
    );
    this.#sparks = Array.from({ length: 30 }, (_, index) =>
      this.add
        .rectangle(0, 0, index % 4 === 0 ? 4 : 2, index % 3 === 0 ? 7 : 4, index % 2 === 0 ? 0xb7fff3 : 0xffd087, 0)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(15),
    );
    this.#ceremonyBars = Array.from({ length: 5 }, (_, index) =>
      this.add
        .rectangle(0, 0, index === 2 ? 3 : 2, 120, index % 2 === 0 ? 0xffd184 : 0x9ffff0, 0)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(14),
    );
    this.#flash = this.add.rectangle(0, 0, 10, 10, 0xeafff8, 0).setOrigin(0).setDepth(16);
    this.#roleWash = this.add.rectangle(0, 0, 10, 10, 0x9ffff0, 0).setOrigin(0).setDepth(17);

    this.scale.on(Phaser.Scale.Events.RESIZE, this.#layout, this);
    this.#layout({ width: this.scale.width, height: this.scale.height } as Phaser.Structs.Size);
    this.#applyVisualState(false);
    this.#callbacks.onReady();
    this.time.delayedCall(0, () => this.#restartAmbientMotion());
  }

  override update(time: number): void {
    if (this.#visualState.paused) return;
    this.#updateTargetCues(time);
    if (this.#activeShot !== null) this.#advanceShot(this.#activeShot, time);
  }

  setVisualState(visualState: PenaltyArenaVisualState): void {
    const previousStatus = this.#visualState.match.status;
    const nextMatchId = visualState.match.matchId;
    const matchChanged = nextMatchId !== this.#presentedMatchId;
    this.#visualState = visualState;

    if (matchChanged) {
      this.#presentedMatchId = nextMatchId;
      this.#lastPresentedTurn = -1;
      this.#pendingResult = null;
      if (this.sys.isActive()) {
        this.#stopShotMotion();
        this.#resetActors();
      }
    }
    if (!this.sys.isActive()) return;
    if (visualState.paused) this.scene.pause();
    else if (this.scene.isPaused()) this.scene.resume();

    if (this.#activeShot === null) {
      const roleChanged = roleFromMatch(visualState.match) !== this.#visualRole;
      if (matchChanged || roleChanged || previousStatus !== visualState.match.status) this.#applyVisualState(true);
    }
    this.#setTargetActivity();
  }

  setPaused(paused: boolean): void {
    this.#visualState = { ...this.#visualState, paused };
    if (!this.sys.isActive()) return;
    if (paused) this.scene.pause();
    else if (this.scene.isPaused()) this.scene.resume();
  }

  playMatchFound(): void {
    if (!this.sys.isActive()) return;
    const actors = this.#visualRole === "shooter"
      ? [this.#attackKeeperReady, this.#playerStriker]
      : [this.#opponentStrikerWindup, this.#defenseKeeperReady];
    this.tweens.killTweensOf(actors);
    if (this.#visualState.reducedMotion) {
      actors.forEach((actor) => actor.setAlpha(1));
      return;
    }
    actors.forEach((actor, index) => actor.setAlpha(0).setY(actor.y + 14 + index * 5));
    this.tweens.add({
      targets: actors,
      y: "-=18",
      alpha: 1,
      duration: 620,
      stagger: 80,
      ease: "Cubic.easeOut",
    });
    this.#ceremonyBars.forEach((bar, index) => {
      bar.setPosition(this.#layoutWidth * (0.22 + index * 0.14), this.#layoutHeight * 0.45).setAlpha(0.34).setScale(1, 0.1);
      this.tweens.add({ targets: bar, scaleY: 1, alpha: 0, duration: 680, delay: index * 45, ease: "Cubic.easeOut" });
    });
  }

  playTurn(result: PvpTurnResult): void {
    if (!this.sys.isActive()) return;
    const matchId = this.#visualState.match.matchId;
    if (matchId !== this.#presentedMatchId) {
      this.#presentedMatchId = matchId;
      this.#lastPresentedTurn = -1;
    }
    if (result.index <= this.#lastPresentedTurn) return;
    this.#lastPresentedTurn = result.index;

    this.#stopShotMotion();
    if (this.#visualRole !== result.role) this.#switchRole(result.role, false);
    this.#resetActors();

    const shotZone = result.role === "shooter" ? result.playerZone : result.opponentZone;
    const keeperZone = result.role === "keeper" ? result.playerZone : result.opponentZone;
    const start = result.role === "shooter" ? { ...this.#attackBallHome } : { ...this.#defenseBallHome };
    const target = this.#targetPoint(result.role, shotZone);
    const keeperStart = result.role === "shooter" ? { ...this.#attackKeeperHome } : { ...this.#defenseKeeperHome };
    const keeperTarget = this.#keeperTargetPoint(result.role, keeperZone);
    const timing = arenaShotTiming(result.role, this.#visualState.reducedMotion);
    const durationSeconds = timing.flightMs / 1_000;
    const gravity = this.#layoutHeight * (result.role === "shooter" ? 0.82 : 0.48);
    const initialVelocity = {
      x: (target.x - start.x) / durationSeconds,
      y: (target.y - start.y - 0.5 * gravity * durationSeconds * durationSeconds) / durationSeconds,
    };
    const shot: ActiveShot = {
      result,
      role: result.role,
      startedAt: this.time.now + timing.windupMs,
      durationMs: timing.flightMs,
      impactHoldMs: timing.impactHoldMs,
      keeperDelayMs: timing.keeperDelayMs,
      keeperMoveMs: timing.keeperMoveMs,
      start,
      target,
      keeperStart,
      keeperTarget,
      initialVelocity,
      gravity,
      saved: result.outcome === "save",
      completed: false,
    };
    this.#activeShot = shot;
    this.#showFlightTarget(shotZone);

    if (result.role === "shooter") {
      const base = this.#playerStrikerScale();
      this.#playerStriker.setAngle(-4).setScale(base * 0.98);
      this.tweens.add({
        targets: this.#playerStriker,
        x: this.#attackStrikerHome.x + 7,
        angle: 5,
        scaleX: base * 1.03,
        scaleY: base * 1.03,
        duration: Math.max(100, timing.windupMs - 45),
        ease: "Cubic.easeIn",
      });
    } else {
      const base = this.#opponentWindupScale();
      this.#opponentStrikerWindup.setAngle(-2).setScale(base * 0.99);
      this.tweens.add({
        targets: this.#opponentStrikerWindup,
        x: this.#defenseOpponentHome.x + 5,
        angle: 2,
        duration: Math.max(100, timing.windupMs - 50),
        ease: "Cubic.easeIn",
      });
      const lead = this.#visualState.reducedMotion ? 35 : 125;
      this.#contactTimer = this.time.delayedCall(Math.max(0, timing.windupMs - lead), () => {
        if (this.#activeShot !== shot) return;
        this.#opponentStrikerWindup.setVisible(false);
        this.#opponentStrikerContact.setVisible(true).setAlpha(1);
      });
    }
  }

  playResult(result: PvpMatchState["result"]): void {
    if (!this.sys.isActive() || result === null || result === "cancelled") return;
    if (this.#activeShot !== null) {
      this.#pendingResult = result;
      return;
    }
    this.#playResultNow(result);
  }

  #playResultNow(result: Exclude<PvpMatchState["result"], null | "cancelled">): void {
    this.#stopShotMotion();
    this.#resetActors();
    const color = result === "win" ? 0xffd17e : result === "draw" ? 0xa4fff1 : 0x75b7be;
    this.#ceremonyBars.forEach((bar, index) => {
      bar
        .setFillStyle(color, 1)
        .setPosition(this.#layoutWidth * (0.19 + index * 0.155), this.#layoutHeight * 0.47)
        .setScale(1, result === "loss" ? 0.35 : 0.7)
        .setAlpha(result === "loss" ? 0.14 : 0.36);
      if (!this.#visualState.reducedMotion) {
        this.tweens.add({
          targets: bar,
          scaleY: result === "loss" ? 0.7 : 1.25,
          alpha: 0,
          duration: 900,
          delay: index * 55,
          ease: "Cubic.easeOut",
        });
      }
    });
    if (result === "win") this.#emitBurst({ x: this.#layoutWidth / 2, y: this.#layoutHeight * 0.46 }, 26);
  }

  debugSnapshot(): Record<string, unknown> {
    const playerFoot = this.#socketPoint(this.#playerStriker, PLAYER_STRIKER_FOOT);
    const opponentSetup = this.#socketPoint(this.#opponentStrikerWindup, OPPONENT_WINDUP_SUPPORT);
    const opponentContact = this.#socketPoint(this.#opponentStrikerContact, OPPONENT_CONTACT_SUPPORT);
    const targets = arenaTargetLayout(this.#visualRole, this.#layoutWidth, this.#layoutHeight);
    const shotPhase = this.#activeShot === null
      ? "idle"
      : this.#activeShot.completed
        ? "impact"
        : this.time.now < this.#activeShot.startedAt
          ? "windup"
          : "flight";
    const relevantKeeper = this.#visualRole === "shooter" ? this.#attackKeeperReady : this.#defenseKeeperReady;
    const defenseDive = this.#defenseKeeperDiveLeft.visible
      ? this.#defenseKeeperDiveLeft
      : this.#defenseKeeperDiveRight.visible
        ? this.#defenseKeeperDiveRight
        : this.#defenseKeeperCentre;
    const attackAction = this.#attackKeeperCentre.visible ? this.#attackKeeperCentre : this.#attackKeeperDive;
    const relevantDive = this.#visualRole === "shooter" ? attackAction : defenseDive;
    return {
      role: this.#visualRole,
      matchId: this.#presentedMatchId,
      lastPresentedTurn: this.#lastPresentedTurn,
      layout: { width: this.#layoutWidth, height: this.#layoutHeight },
      targets: targets.map((target) => ({ ...target })),
      attackKeeperHome: { ...this.#attackKeeperHome },
      attackKeeperGroundY: this.#attackKeeperHome.y + this.#attackKeeperReady.displayHeight / 2,
      attackGoalGroundY: this.#goalNet.y + this.#goalNet.displayHeight / 2,
      strikerHome: { ...this.#attackStrikerHome },
      strikerFoot: playerFoot,
      attackBallHome: { ...this.#attackBallHome },
      footBallDistance: Math.hypot(playerFoot.x - this.#attackBallHome.x, playerFoot.y - this.#attackBallHome.y),
      defenseOpponentHome: { ...this.#defenseOpponentHome },
      defenseOpponentContactHome: { ...this.#defenseOpponentContactHome },
      opponentSetupFoot: opponentSetup,
      opponentContactFoot: opponentContact,
      opponentGroundY: this.#defenseOpponentGroundY(),
      opponentSetupGroundDistance: Math.abs(opponentSetup.y - this.#defenseOpponentGroundY()),
      opponentContactGroundDistance: Math.abs(opponentContact.y - this.#defenseOpponentGroundY()),
      defenseBallHome: { ...this.#defenseBallHome },
      defenseGoalLineY: this.#defenseGoalLineY(),
      defenseKeeperGroundY: this.#defenseKeeperHome.y + this.#defenseKeeperReady.displayHeight / 2,
      leftGloveHome: { ...this.#defenseKeeperHome },
      rightGloveHome: { ...this.#defenseKeeperHome },
      gloves: {
        left: { x: relevantDive.visible ? relevantDive.x : relevantKeeper.x, y: relevantDive.visible ? relevantDive.y : relevantKeeper.y, alpha: 1 },
        right: { x: relevantDive.visible ? relevantDive.x : relevantKeeper.x, y: relevantDive.visible ? relevantDive.y : relevantKeeper.y, alpha: 1 },
      },
      keeper: { x: relevantDive.visible ? relevantDive.x : relevantKeeper.x, y: relevantDive.visible ? relevantDive.y : relevantKeeper.y, visible: true },
      keeperDiveVisible: relevantDive.visible,
      keeperDiveDirection: this.#visualRole === "shooter"
        ? this.#attackKeeperCentre.visible
          ? "centre"
          : this.#attackKeeperDive.visible
            ? this.#attackKeeperDive.flipX
              ? "right"
              : "left"
            : "none"
        : this.#defenseKeeperDiveLeft.visible
          ? "left"
          : this.#defenseKeeperDiveRight.visible
            ? "right"
            : this.#defenseKeeperCentre.visible
              ? "centre"
              : "none",
      opponentPose: this.#opponentStrikerContact.visible ? "contact" : "windup",
      shotPhase,
      targetCues: this.#targetCues.map((cue) => {
        const glyphBounds = cue.glyph.getBounds();
        const plateBounds = cue.plate.getBounds();
        return {
          alpha: cue.root.alpha,
          scale: cue.root.scaleX,
          glyph: cue.direction,
          glyphCenterDeltaX: Math.abs(glyphBounds.centerX - plateBounds.centerX),
          glyphCenterDeltaY: Math.abs(glyphBounds.centerY - plateBounds.centerY),
        };
      }),
      goalNet: {
        x: this.#goalNet.x,
        y: this.#goalNet.y,
        displayWidth: this.#goalNet.displayWidth,
        displayHeight: this.#goalNet.displayHeight,
        visible: this.#goalNet.visible,
      },
      ball: { x: this.#ball.x, y: this.#ball.y, displayWidth: this.#ball.displayWidth, displayHeight: this.#ball.displayHeight },
    };
  }

  #createTargetCue(direction: TargetCue["direction"]): TargetCue {
    const halo = this.add.circle(0, 0, 42, 0x063e46, 0.2).setStrokeStyle(1.4, 0xffd89a, 0.56);
    const plate = this.add.circle(0, 0, 34, 0x08696f, 0.28).setStrokeStyle(2.2, 0xb9fff5, 0.96);
    const glyph = direction === "centre"
      ? this.add.triangle(0, 0, 0, 27, 30, 27, 15, 0, 0xbafff4, 0.94)
      : direction === "left"
        ? this.add.triangle(0, 0, 27, 0, 27, 30, 0, 15, 0xbafff4, 0.94)
        : this.add.triangle(0, 0, 0, 0, 0, 30, 27, 15, 0xbafff4, 0.94);
    glyph.setBlendMode(Phaser.BlendModes.ADD);
    const root = this.add.container(0, 0, [halo, plate, glyph]).setDepth(8).setVisible(false);
    return { root, halo, plate, glyph, direction, baseScale: 1 };
  }

  #layout(size: Phaser.Structs.Size): void {
    const physicalWidth = Math.round(size.width);
    const physicalHeight = Math.round(size.height);
    const width = Math.round(physicalWidth / this.#renderScale);
    const height = Math.round(physicalHeight / this.#renderScale);
    if (width <= 0 || height <= 0 || (width === this.#layoutWidth && height === this.#layoutHeight)) return;
    this.#layoutWidth = width;
    this.#layoutHeight = height;
    this.cameras.main.setViewport(0, 0, physicalWidth, physicalHeight).setZoom(this.#renderScale).centerOn(width / 2, height / 2);

    const attackBackground = arenaBackgroundLayout("shooter", width, height);
    this.#attackBackground.setPosition(attackBackground.x, attackBackground.y).setScale(attackBackground.scale);
    const defenseBackground = arenaBackgroundLayout("keeper", width, height);
    this.#defenseBackground.setPosition(defenseBackground.x, defenseBackground.y).setScale(defenseBackground.scale);
    this.#shade.setSize(width, height);
    this.#flash.setSize(width, height);
    this.#roleWash.setSize(width, height);

    const attackGoalY = arenaTargetLayout("shooter", width, height)[1]?.y ?? height * 0.475;
    const goalScale = (width * 0.94) / ARENA_ART.goalNet.width;
    const attackKeeperHeight = ARENA_ART.goalkeeperReady.height * this.#attackKeeperScale();
    const attackGoalGroundY = attackGoalY + (ARENA_ART.goalNet.height * goalScale) / 2;
    this.#attackKeeperHome = { x: width * 0.5, y: attackGoalGroundY - attackKeeperHeight / 2 };
    this.#attackBallHome = { x: width * 0.5, y: height * 0.815 };
    const strikerSize = this.#displaySize(ARENA_ART.strikerKick.width, ARENA_ART.strikerKick.height, this.#playerStrikerScale());
    this.#attackStrikerHome = {
      x: this.#attackBallHome.x - (PLAYER_STRIKER_FOOT.x - 0.5) * strikerSize.width,
      y: this.#attackBallHome.y - (PLAYER_STRIKER_FOOT.y - 0.5) * strikerSize.height,
    };

    const defenseKeeperHeight = ARENA_ART.defenseGoalkeeperReady.height * this.#defenseKeeperReadyScale();
    this.#defenseKeeperHome = { x: width * 0.5, y: this.#defenseGoalLineY() - defenseKeeperHeight / 2 };
    const defenseBallSize = this.#displaySize(ARENA_ART.ball.width, ARENA_ART.ball.height, this.#defenseBallScale());
    this.#defenseBallHome = { x: width * 0.54, y: this.#defenseOpponentGroundY() - defenseBallSize.height * 0.46 };
    const opponentSupportX = width * 0.47;
    const windupSize = this.#displaySize(
      ARENA_ART.opponentStrikerWindup.width,
      ARENA_ART.opponentStrikerWindup.height,
      this.#opponentWindupScale(),
    );
    this.#defenseOpponentHome = {
      x: opponentSupportX - (OPPONENT_WINDUP_SUPPORT.x - 0.5) * windupSize.width,
      y: this.#defenseOpponentGroundY() - (OPPONENT_WINDUP_SUPPORT.y - 0.5) * windupSize.height,
    };
    const contactSize = this.#displaySize(
      ARENA_ART.opponentStrikerContact.width,
      ARENA_ART.opponentStrikerContact.height,
      this.#opponentContactScale(),
    );
    this.#defenseOpponentContactHome = {
      x: opponentSupportX - (OPPONENT_CONTACT_SUPPORT.x - 0.5) * contactSize.width,
      y: this.#defenseOpponentGroundY() - (OPPONENT_CONTACT_SUPPORT.y - 0.5) * contactSize.height,
    };

    this.#layoutGoal();
    this.#layoutTargets(this.#visualRole);
    this.#resetActors();
    this.#restartAmbientMotion();
  }

  #layoutGoal(): void {
    const visible = this.#visualRole === "shooter";
    const widthRatio = 0.94;
    const scale = (this.#layoutWidth * widthRatio) / ARENA_ART.goalNet.width;
    const goalY = arenaTargetLayout("shooter", this.#layoutWidth, this.#layoutHeight)[1]?.y
      ?? this.#layoutHeight * 0.475;
    this.#goalNet
      .setPosition(this.#layoutWidth / 2, goalY)
      .setScale(scale)
      .setAlpha(visible ? 0.96 : 0)
      .setVisible(visible);
  }

  #layoutTargets(role: PvpRole): void {
    const targets = arenaTargetLayout(role, this.#layoutWidth, this.#layoutHeight);
    targets.forEach((target, index) => {
      const cue = this.#targetCues[index];
      if (cue === undefined) return;
      cue.baseScale = target.radius / 42;
      cue.root.setPosition(target.x, target.y).setScale(cue.baseScale).setDepth(role === "keeper" ? 14 : 8);
    });
    this.#callbacks.onTargetLayout({ role, targets: targets.map((target) => ({ ...target })) });
  }

  #targetPoint(role: PvpRole, zone: number): Point {
    const targets = arenaTargetLayout(role, this.#layoutWidth, this.#layoutHeight);
    const target = targets[clampZone(zone)] ?? targets[1];
    return target === undefined ? { x: this.#layoutWidth / 2, y: this.#layoutHeight * 0.48 } : { x: target.x, y: target.y };
  }

  #keeperTargetPoint(role: PvpRole, zone: number): Point {
    const target = this.#targetPoint(role, zone);
    if (role === "shooter") {
      return {
        x: this.#attackKeeperHome.x + (target.x - this.#attackKeeperHome.x) * 0.82,
        y: this.#attackKeeperHome.y + (target.y - this.#attackKeeperHome.y) * 0.58,
      };
    }
    const clamped = clampZone(zone);
    return {
      x: clamped === 1 ? this.#defenseKeeperHome.x : target.x,
      y: clamped === 1 ? this.#defenseKeeperHome.y - this.#layoutHeight * 0.1 : this.#defenseKeeperHome.y - this.#layoutHeight * 0.08,
    };
  }

  #displaySize(width: number, height: number, scale: number): { width: number; height: number } {
    return { width: width * scale, height: height * scale };
  }

  #attackKeeperScale(): number {
    return Math.min((this.#layoutWidth * 0.23) / ARENA_ART.goalkeeperReady.width, (this.#layoutHeight * 0.17) / ARENA_ART.goalkeeperReady.height);
  }

  #attackDiveScale(): number {
    return Math.min((this.#layoutWidth * 0.4) / ARENA_ART.goalkeeperDive.width, (this.#layoutHeight * 0.14) / ARENA_ART.goalkeeperDive.height);
  }

  #attackCentreScale(): number {
    return Math.min(
      (this.#layoutWidth * 0.21) / ARENA_ART.goalkeeperCentreSave.width,
      (this.#layoutHeight * 0.19) / ARENA_ART.goalkeeperCentreSave.height,
    );
  }

  #playerStrikerScale(): number {
    return Math.min((this.#layoutWidth * 0.36) / ARENA_ART.strikerKick.width, (this.#layoutHeight * 0.23) / ARENA_ART.strikerKick.height);
  }

  #opponentWindupScale(): number {
    return Math.min((this.#layoutWidth * 0.21) / ARENA_ART.opponentStrikerWindup.width, (this.#layoutHeight * 0.145) / ARENA_ART.opponentStrikerWindup.height);
  }

  #opponentContactScale(): number {
    return Math.min((this.#layoutWidth * 0.24) / ARENA_ART.opponentStrikerContact.width, (this.#layoutHeight * 0.145) / ARENA_ART.opponentStrikerContact.height);
  }

  #defenseKeeperReadyScale(): number {
    return Math.min((this.#layoutWidth * 0.42) / ARENA_ART.defenseGoalkeeperReady.width, (this.#layoutHeight * 0.26) / ARENA_ART.defenseGoalkeeperReady.height);
  }

  #defenseDiveScale(side: "left" | "right"): number {
    const asset = side === "left" ? ARENA_ART.defenseGoalkeeperDiveLeft : ARENA_ART.defenseGoalkeeperDiveRight;
    return Math.min((this.#layoutWidth * 0.58) / asset.width, (this.#layoutHeight * 0.27) / asset.height);
  }

  #defenseCentreScale(): number {
    return Math.min((this.#layoutWidth * 0.27) / ARENA_ART.defenseGoalkeeperBlockCentre.width, (this.#layoutHeight * 0.31) / ARENA_ART.defenseGoalkeeperBlockCentre.height);
  }

  #attackBallScale(): number {
    return Math.min((this.#layoutWidth * 0.086) / ARENA_ART.ball.width, (this.#layoutHeight * 0.043) / ARENA_ART.ball.height);
  }

  #defenseBallScale(): number {
    return Math.min((this.#layoutWidth * 0.046) / ARENA_ART.ball.width, (this.#layoutHeight * 0.023) / ARENA_ART.ball.height);
  }

  #defenseGoalLineY(): number {
    return this.#layoutHeight * (this.#layoutHeight < 700 ? 0.91 : 0.84);
  }

  #defenseOpponentGroundY(): number {
    return this.#layoutHeight * 0.57;
  }

  #socketPoint(image: Phaser.GameObjects.Image, socket: Point): Point {
    return {
      x: image.x + (socket.x - image.originX) * image.displayWidth,
      y: image.y + (socket.y - image.originY) * image.displayHeight,
    };
  }

  #resetActors(): void {
    this.#activeShot = null;
    this.#contactTimer?.remove(false);
    this.#contactTimer = null;
    this.tweens.killTweensOf([
      this.#attackKeeperReady,
      this.#attackKeeperDive,
      this.#attackKeeperCentre,
      this.#playerStriker,
      this.#opponentStrikerWindup,
      this.#opponentStrikerContact,
      this.#defenseKeeperReady,
      this.#defenseKeeperDiveLeft,
      this.#defenseKeeperDiveRight,
      this.#defenseKeeperCentre,
      this.#ball,
      this.#goalNet,
    ]);

    this.#attackKeeperReady
      .setPosition(this.#attackKeeperHome.x, this.#attackKeeperHome.y)
      .setScale(this.#attackKeeperScale())
      .setAlpha(1)
      .setAngle(0)
      .setFlipX(false)
      .setVisible(true);
    this.#attackKeeperDive.setPosition(this.#attackKeeperHome.x, this.#attackKeeperHome.y).setScale(this.#attackDiveScale()).setVisible(false).setFlipX(false).setAngle(0);
    this.#attackKeeperCentre
      .setPosition(this.#attackKeeperHome.x, this.#attackKeeperHome.y)
      .setScale(this.#attackCentreScale())
      .setAlpha(1)
      .setAngle(0)
      .setVisible(false);
    this.#playerStriker.setPosition(this.#attackStrikerHome.x, this.#attackStrikerHome.y).setScale(this.#playerStrikerScale()).setAlpha(1).setAngle(0);

    this.#opponentStrikerWindup
      .setPosition(this.#defenseOpponentHome.x, this.#defenseOpponentHome.y)
      .setScale(this.#opponentWindupScale())
      .setAlpha(1)
      .setAngle(0)
      .setVisible(true);
    this.#opponentStrikerContact
      .setPosition(this.#defenseOpponentContactHome.x, this.#defenseOpponentContactHome.y)
      .setScale(this.#opponentContactScale())
      .setAlpha(0)
      .setAngle(0)
      .setVisible(false);
    this.#defenseKeeperReady
      .setPosition(this.#defenseKeeperHome.x, this.#defenseKeeperHome.y)
      .setScale(this.#defenseKeeperReadyScale())
      .setAlpha(1)
      .setAngle(0)
      .setVisible(true);
    this.#defenseKeeperDiveLeft
      .setPosition(this.#defenseKeeperHome.x, this.#defenseKeeperHome.y)
      .setScale(this.#defenseDiveScale("left"))
      .setAlpha(1)
      .setFlipX(false)
      .setAngle(0)
      .setVisible(false);
    this.#defenseKeeperDiveRight
      .setPosition(this.#defenseKeeperHome.x, this.#defenseKeeperHome.y)
      .setScale(this.#defenseDiveScale("right"))
      .setAlpha(1)
      .setFlipX(false)
      .setAngle(0)
      .setVisible(false);
    this.#defenseKeeperCentre
      .setPosition(this.#defenseKeeperHome.x, this.#defenseKeeperHome.y)
      .setScale(this.#defenseCentreScale())
      .setAlpha(1)
      .setVisible(false);

    const ballHome = this.#visualRole === "shooter" ? this.#attackBallHome : this.#defenseBallHome;
    this.#ball
      .setPosition(ballHome.x, ballHome.y)
      .setScale(this.#visualRole === "shooter" ? this.#attackBallScale() : this.#defenseBallScale())
      .setAngle(0)
      .setAlpha(1)
      .setDepth(this.#visualRole === "shooter" ? 10 : 2.5);
    this.#trail.forEach((particle) => particle.setAlpha(0));
    this.#sparks.forEach((particle) => particle.setAlpha(0));
    this.#setRoleVisibility();
  }

  #setRoleVisibility(): void {
    const attack = this.#visualRole === "shooter";
    this.#attackBackground.setAlpha(attack ? 1 : 0);
    this.#defenseBackground.setAlpha(attack ? 0 : 1);
    this.#attackKeeperReady.setVisible(attack);
    this.#attackKeeperDive.setVisible(false);
    this.#attackKeeperCentre.setVisible(false);
    this.#playerStriker.setVisible(attack);
    this.#opponentStrikerWindup.setVisible(!attack);
    this.#opponentStrikerContact.setVisible(false);
    this.#defenseKeeperReady.setVisible(!attack);
    this.#defenseKeeperDiveLeft.setVisible(false);
    this.#defenseKeeperDiveRight.setVisible(false);
    this.#defenseKeeperCentre.setVisible(false);
    this.#layoutGoal();
  }

  #switchRole(role: PvpRole, animate: boolean): void {
    if (role === this.#visualRole) {
      this.#layoutTargets(role);
      return;
    }
    const previousRole = this.#visualRole;
    this.#visualRole = role;
    this.#layoutGoal();
    this.#layoutTargets(role);
    this.#resetActors();
    if (!animate || this.#visualState.reducedMotion) {
      this.#restartAmbientMotion();
      return;
    }
    const incoming = role === "shooter" ? this.#attackBackground : this.#defenseBackground;
    const outgoing = previousRole === "shooter" ? this.#attackBackground : this.#defenseBackground;
    incoming.setAlpha(0);
    outgoing.setAlpha(1);
    this.#roleWash.setFillStyle(role === "shooter" ? 0xffd58c : 0xa2fff1, 1).setAlpha(0.16);
    this.tweens.add({ targets: incoming, alpha: 1, duration: 430, ease: "Sine.easeOut" });
    this.tweens.add({ targets: outgoing, alpha: 0, duration: 380, ease: "Sine.easeIn" });
    this.tweens.add({ targets: this.#roleWash, alpha: 0, duration: 500, ease: "Quad.easeOut" });
  }

  #applyVisualState(animate: boolean): void {
    if (this.#activeShot !== null) return;
    const role = roleFromMatch(this.#visualState.match);
    if (role !== this.#visualRole) this.#switchRole(role, animate);
    else {
      this.#resetActors();
      this.#layoutTargets(role);
    }
    const choosing = this.#visualState.match.status === "choosing" || this.#visualState.match.status === "move-accepted";
    this.#shade.setAlpha(choosing ? 0.045 : 0.11);
    this.#setTargetActivity();
    this.#restartAmbientMotion();
  }

  #setTargetActivity(): void {
    const choosing = this.#visualState.match.status === "choosing";
    const locked = this.#visualState.match.status === "move-accepted";
    const selected = this.#visualState.match.currentTurn?.playerZone ?? null;
    const visible = choosing || locked;
    this.#targetCues.forEach((cue, index) => {
      const isSelected = selected === index;
      cue.root.setVisible(visible);
      cue.plate
        .setFillStyle(isSelected ? 0x9d6f32 : 0x076a70, isSelected ? 0.42 : 0.22)
        .setStrokeStyle(isSelected ? 2.6 : 2, isSelected ? 0xffd58d : 0xc4fff6, isSelected ? 1 : 0.94);
      cue.halo
        .setFillStyle(isSelected ? 0x8a612e : 0x063e46, isSelected ? 0.3 : 0.16)
        .setStrokeStyle(isSelected ? 1.8 : 1.4, 0xffd89a, isSelected ? 0.94 : 0.52);
      cue.glyph.setFillStyle(isSelected ? 0xffd58d : 0x9ffff0, isSelected ? 1 : 0.84);
      if (locked && !isSelected) cue.root.setAlpha(0.14);
      else cue.root.setAlpha(1);
    });
  }

  #updateTargetCues(time: number): void {
    if (this.#activeShot !== null) return;
    const choosing = this.#visualState.match.status === "choosing";
    const locked = this.#visualState.match.status === "move-accepted";
    if (!choosing && !locked) return;
    const selected = this.#visualState.match.currentTurn?.playerZone ?? null;
    this.#targetCues.forEach((cue, index) => {
      const isSelected = selected === index;
      const phase = this.#visualState.reducedMotion
        ? 0.55
        : 0.5 + 0.5 * Math.sin((time / ARENA_PRESENTATION.cuePulseMs) * Math.PI * 2 - index * 0.66);
      const scale = cue.baseScale * (isSelected ? 1.08 : locked ? 0.96 : 0.96 + phase * 0.075);
      cue.root.setScale(scale);
      cue.glyph.setAlpha(isSelected ? 1 : 0.76 + phase * 0.24);
      if (!locked || isSelected) cue.root.setAlpha(isSelected ? 1 : 0.82 + phase * 0.18);
    });
  }

  #showFlightTarget(zone: number): void {
    const selected = clampZone(zone);
    this.#targetCues.forEach((cue, index) => {
      const visible = index === selected;
      cue.root
        .setVisible(visible)
        .setAlpha(visible ? 0.82 : 0)
        .setScale(cue.baseScale * 1.03)
        .setDepth(this.#visualRole === "shooter" ? 4 : 9);
      if (visible) {
        this.tweens.add({
          targets: cue.root,
          alpha: this.#visualState.reducedMotion ? 0.16 : 0.06,
          scale: cue.baseScale * 1.12,
          duration: this.#visualState.reducedMotion ? 90 : ARENA_PRESENTATION.windupMs,
          ease: "Cubic.easeOut",
        });
      }
    });
  }

  #advanceShot(shot: ActiveShot, time: number): void {
    const elapsedMs = time - shot.startedAt;
    if (elapsedMs < 0) return;
    const elapsed = Math.min(shot.durationMs, elapsedMs) / 1_000;
    const progress = Phaser.Math.Clamp(elapsedMs / shot.durationMs, 0, 1);
    const x = shot.start.x + shot.initialVelocity.x * elapsed;
    const y = shot.start.y + shot.initialVelocity.y * elapsed + 0.5 * shot.gravity * elapsed * elapsed;
    const homeScale = shot.role === "shooter" ? this.#attackBallScale() : this.#defenseBallScale();
    const perspective = shot.role === "shooter" ? 1 - progress * 0.45 : 1 + progress * 1.05;
    this.#ball
      .setPosition(x, y)
      .setScale(homeScale * perspective)
      .setDepth(shot.role === "keeper" && progress < 0.28 ? 2.5 : 10)
      .setAngle(this.#visualState.reducedMotion ? 0 : progress * (shot.role === "shooter" ? 560 : -610));
    if (!this.#visualState.reducedMotion) this.#updateTrail(shot, progress);

    const keeperElapsed = elapsedMs - shot.keeperDelayMs;
    if (keeperElapsed >= 0) {
      const keeperProgress = Phaser.Math.Clamp(keeperElapsed / shot.keeperMoveMs, 0, 1);
      if (shot.role === "shooter") this.#moveAttackKeeper(shot, keeperProgress);
      else this.#moveDefenseKeeper(shot, keeperProgress);
    }
    if (progress >= 1 && !shot.completed) this.#finishShot(shot);
  }

  #moveAttackKeeper(shot: ActiveShot, rawProgress: number): void {
    const progress = easeOutCubic(rawProgress);
    const zone = clampZone(shot.result.opponentZone);
    const deltaX = shot.keeperTarget.x - shot.keeperStart.x;
    const deltaY = shot.keeperTarget.y - shot.keeperStart.y;
    if (zone === 1) {
      this.#attackKeeperDive.setVisible(false);
      const launched = progress >= 0.12;
      const lift = Math.sin(progress * Math.PI) * this.#layoutHeight * 0.055;
      this.#attackKeeperReady
        .setVisible(!launched)
        .setAlpha(1)
        .setPosition(shot.keeperStart.x, shot.keeperStart.y + Math.min(progress / 0.12, 1) * 3)
        .setScale(this.#attackKeeperScale() * (1 - Math.min(progress / 0.12, 1) * 0.04));
      this.#attackKeeperCentre
        .setVisible(launched)
        .setAlpha(1)
        .setPosition(
          shot.keeperStart.x,
          shot.keeperStart.y + deltaY * progress - lift + this.#layoutHeight * 0.012,
        )
        .setScale(this.#attackCentreScale() * (0.94 + Math.sin(progress * Math.PI) * 0.09));
      return;
    }
    this.#attackKeeperReady.setVisible(false);
    this.#attackKeeperCentre.setVisible(false);
    this.#attackKeeperDive
      .setVisible(true)
      .setFlipX(zone === 2)
      .setPosition(
        shot.keeperStart.x + deltaX * progress,
        shot.keeperStart.y + deltaY * progress - Math.sin(progress * Math.PI) * this.#layoutHeight * 0.02,
      )
      .setScale(this.#attackDiveScale())
      .setAngle((zone === 2 ? 1 : -1) * Phaser.Math.Linear(5, 0, progress));
  }

  #moveDefenseKeeper(shot: ActiveShot, rawProgress: number): void {
    const progress = easeOutCubic(rawProgress);
    const zone = clampZone(shot.result.playerZone);
    this.#defenseKeeperReady.setVisible(false);
    if (zone === 1) {
      this.#defenseKeeperDiveLeft.setVisible(false);
      this.#defenseKeeperDiveRight.setVisible(false);
      this.#defenseKeeperCentre
        .setVisible(true)
        .setPosition(
          shot.keeperStart.x,
          Phaser.Math.Linear(shot.keeperStart.y, shot.keeperTarget.y, progress) - Math.sin(progress * Math.PI) * 12,
        )
        .setScale(this.#defenseCentreScale() * (0.96 + progress * 0.04));
      return;
    }
    this.#defenseKeeperCentre.setVisible(false);
    const dive = zone === 0 ? this.#defenseKeeperDiveLeft : this.#defenseKeeperDiveRight;
    const otherDive = zone === 0 ? this.#defenseKeeperDiveRight : this.#defenseKeeperDiveLeft;
    otherDive.setVisible(false);
    dive
      .setVisible(true)
      .setFlipX(false)
      .setPosition(
        Phaser.Math.Linear(shot.keeperStart.x, shot.keeperTarget.x, progress),
        Phaser.Math.Linear(shot.keeperStart.y, shot.keeperTarget.y, progress) - Math.sin(progress * Math.PI) * this.#layoutHeight * 0.055,
      )
      .setScale(this.#defenseDiveScale(zone === 0 ? "left" : "right"))
      .setAngle(0);
  }

  #updateTrail(shot: ActiveShot, progress: number): void {
    this.#trail.forEach((particle, index) => {
      const lag = (index + 1) * 0.027;
      const trailProgress = Math.max(0, progress - lag);
      if (trailProgress <= 0) {
        particle.setAlpha(0);
        return;
      }
      const elapsed = (trailProgress * shot.durationMs) / 1_000;
      const x = shot.start.x + shot.initialVelocity.x * elapsed;
      const y = shot.start.y + shot.initialVelocity.y * elapsed + 0.5 * shot.gravity * elapsed * elapsed;
      particle
        .setPosition(x, y)
        .setRotation(Math.atan2(shot.target.y - shot.start.y, shot.target.x - shot.start.x))
        .setAlpha((1 - index / this.#trail.length) * 0.42);
    });
  }

  #finishShot(shot: ActiveShot): void {
    if (shot.completed) return;
    shot.completed = true;
    const finalScale = shot.role === "shooter" ? this.#attackBallScale() * 0.56 : this.#defenseBallScale() * 2.05;
    this.#ball.setPosition(shot.target.x, shot.target.y).setScale(finalScale).setDepth(10);
    if (shot.role === "shooter") this.#moveAttackKeeper(shot, 1);
    else this.#moveDefenseKeeper(shot, 1);
    this.#emitBurst(shot.target, shot.saved ? 22 : 16);
    this.#flash.setFillStyle(shot.saved ? 0xbffff2 : 0xffdfa0, 1).setAlpha(shot.saved ? 0.08 : 0.13);
    this.tweens.add({ targets: this.#flash, alpha: 0, duration: 340, ease: "Quad.easeOut" });

    const cue = this.#targetCues[clampZone(shot.role === "shooter" ? shot.result.playerZone : shot.result.opponentZone)];
    if (cue !== undefined) {
      cue.root.setVisible(true).setAlpha(1).setScale(cue.baseScale * 0.92);
      this.tweens.add({ targets: cue.root, alpha: 0.12, scale: cue.baseScale * 1.28, duration: 520, ease: "Cubic.easeOut" });
    }
    if (!shot.saved && shot.role === "shooter") {
      const baseScale = this.#goalNet.scaleX;
      this.tweens.add({
        targets: this.#goalNet,
        scaleX: baseScale * 1.012,
        scaleY: baseScale * 0.976,
        y: this.#goalNet.y + 3,
        duration: 105,
        yoyo: true,
        repeat: 2,
        ease: "Sine.easeInOut",
      });
    } else if (!this.#visualState.reducedMotion) {
      const direction = shot.target.x < this.#layoutWidth / 2 ? 1 : -1;
      this.tweens.add({
        targets: this.#ball,
        x: shot.target.x + direction * 26,
        y: shot.target.y + 34,
        angle: this.#ball.angle + direction * 150,
        alpha: 0.72,
        duration: 360,
        ease: "Quad.easeOut",
      });
    }
    this.time.delayedCall(shot.impactHoldMs, () => {
      if (this.#activeShot !== shot) return;
      this.#activeShot = null;
      this.#trail.forEach((particle) => particle.setAlpha(0));
      this.#applyVisualState(true);
      const pendingResult = this.#pendingResult;
      this.#pendingResult = null;
      if (pendingResult !== null) this.#playResultNow(pendingResult);
    });
  }

  #emitBurst(point: Point, count: number): void {
    if (this.#visualState.reducedMotion) return;
    this.#sparks.slice(0, count).forEach((spark, index) => {
      const angle = (Math.PI * 2 * index) / count + (index % 3) * 0.16;
      const distance = 26 + (index % 7) * 7;
      spark.setPosition(point.x, point.y).setRotation(angle).setAlpha(0.86).setScale(index % 4 === 0 ? 1.15 : 0.8);
      this.tweens.add({
        targets: spark,
        x: point.x + Math.cos(angle) * distance,
        y: point.y + Math.sin(angle) * distance + 16,
        alpha: 0,
        scale: 0.2,
        duration: 390 + (index % 6) * 34,
        ease: "Cubic.easeOut",
      });
    });
  }

  #restartAmbientMotion(): void {
    this.#ambientTweens.forEach((tween) => tween.stop());
    this.#ambientTweens = [];
    if (this.#visualState.reducedMotion || this.#visualState.paused || this.#activeShot !== null) return;
    const actor = this.#visualRole === "shooter" ? this.#attackKeeperReady : this.#defenseKeeperReady;
    const baseScale = this.#visualRole === "shooter" ? this.#attackKeeperScale() : this.#defenseKeeperReadyScale();
    this.tweens.killTweensOf(actor);
    this.#ambientTweens.push(
      this.tweens.add({ targets: actor, scaleX: baseScale * 1.008, duration: 1_850, yoyo: true, repeat: -1, ease: "Sine.easeInOut" }),
    );
  }

  #stopShotMotion(): void {
    this.#activeShot = null;
    this.#contactTimer?.remove(false);
    this.#contactTimer = null;
    this.#ambientTweens.forEach((tween) => tween.stop());
    this.#ambientTweens = [];
    this.tweens.killTweensOf([
      this.#attackKeeperReady,
      this.#attackKeeperDive,
      this.#attackKeeperCentre,
      this.#playerStriker,
      this.#opponentStrikerWindup,
      this.#opponentStrikerContact,
      this.#defenseKeeperReady,
      this.#defenseKeeperDiveLeft,
      this.#defenseKeeperDiveRight,
      this.#defenseKeeperCentre,
      this.#ball,
      this.#goalNet,
      ...this.#targetCues.map((cue) => cue.root),
      ...this.#trail,
      ...this.#sparks,
      ...this.#ceremonyBars,
    ]);
    this.#trail.forEach((particle) => particle.setAlpha(0));
    this.#sparks.forEach((particle) => particle.setAlpha(0));
  }
}
